import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useQuery } from '@apollo/client';
import { READ_SIMULATIONS_PAGINATED } from '../queries/SimulationQueries';
import withUser from '../HOCs/WithUser';
import StyledPage from '@base/components/StyledPage';
import RedirectWithPrevState from '@base/components/RedirectWithPrevState';
import { SimulationList } from '@base/components/SimulationItem';
import StyledPageHeader from '@base/styled-components/StyledPageHeader';
import { CanViewDashboard, UserTypes } from '@base/utility/Permissions';
import WithOnboardingHandler from '@base/HOCs/WithOnboardingHandler';
import OnboardingProgress from '@base/components/OnboardingProgress';
import QuickActions from '@core/components/Dashboard/QuickActions/QuickActions';
import TrialBanner from '@core/components/Signup/legacy/TrialBanner/TrialBanner';
import useUser from '@core/helpers/useUser';
import SimulationDataCard from '@core/components/SimulationsList/components/SimulationDataCard/SimulationDataCard';
import PendingContentCopyBanner from '@core/components/Banners/PendingContentCopyBanner/PendingContentCopyBanner';
import DashboardUsagePanel from '@core/components/Dashboard/DashboardUsagePanel/DashboardUsagePanel';
import { StyledSectionHeader } from '@core/components/Dashboard/QuickActions/QuickActions.styled';
import { Content } from '@base/pages/DashboardPage.styled';

export const DashboardPage = (props) => {
  const canView = CanViewDashboard();
  const { Organisation, SignupMethod, UserType } = useUser();
  const skipQueries = !canView || !props.userID || !Organisation?.ID;

  const { data: simData } = useQuery(READ_SIMULATIONS_PAGINATED, {
    variables: {
      offset: 0,
      limit: 3,
      sort: { Created: 'DESC' },
      filter: { Organisation: { ID: { eq: Organisation?.ID } } },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: skipQueries,
  });

  const simulations = useMemo(() => {
    const sims = simData?.readSimulations?.edges?.map(({ node }) => node);

    if (!sims) {
      return [];
    }

    return sims;
  }, [simData]);

  const trial = useMemo(() => {
    const method = SignupMethod || 'public';

    return Organisation?.Trial && method === 'public';
  }, [Organisation, SignupMethod]);

  if (!props.userID) {
    return <RedirectWithPrevState to="/login" />;
  }
  if (!canView) {
    let redirectURL = '/simulations';
    if (props.userType === UserTypes.USER) {
      redirectURL = '/home';
    } else if (props.userType === UserTypes.ANALYST) {
      redirectURL = '/analytics-data/overall';
    }
    return (
      <RedirectWithPrevState to={redirectURL} state={{ justLoggedIn: true }} />
    );
  }

  return (
    <StyledPage pageKey="dashboard">
      <OnboardingProgress userID={props.userID} />

      <Content>
        <StyledPageHeader>Dashboard</StyledPageHeader>

        {trial && <TrialBanner />}
        {UserType === 'admin' && <PendingContentCopyBanner />}

        <QuickActions />

        <DashboardUsagePanel skip={skipQueries} />
        {!!simulations?.length && (
          <>
            <StyledSectionHeader>
              Latest interactive videos added
            </StyledSectionHeader>
            <SimulationList>
              {simulations.map((simulation) => (
                <SimulationDataCard
                  simulation={simulation}
                  key={simulation.ID}
                />
              ))}
            </SimulationList>
          </>
        )}
      </Content>
    </StyledPage>
  );
};

DashboardPage.propTypes = {
  userID: PropTypes.number,
  match: PropTypes.object.isRequired,
  userOrganisationID: PropTypes.number,
  userOrganisationName: PropTypes.string,
};

export default _.flowRight(withUser, WithOnboardingHandler)(DashboardPage);
